import { IntlShape } from 'react-intl';

import { MachineContext } from '..';

/** translate with fallback
 *
 * @param {string} messageId - translation keys to be translated
 * @param {IntlShape} intl - react-intl dependency
 * @param {Record<string, any>} varcontents @optional  - list of variable names with values to be formatted into the translated string
 * @param {boolean} replaceVariables - if false, {variables} are not replaced
 * @return {string} translated texts
 */
export const translateWithFallback = (
  intl: IntlShape,
  messageId: string,
  varcontents?: Record<string, any>,
  replaceVariables = true
) => {
  // if we have a translated message for the given text,
  // use it - if not, display the given id directly

  let message = '';

  if (intl.messages[messageId]) {
    // the next line looks strange as we add an unnecessary trailing space.
    // But it is necessary due to https://evoach.atlassian.net/browse/PROD-1243
    // If you leave it out, the functions returns nothing or null because
    // intl.formatMessage doesn't seem to interpret {..} values in the
    // expected way
    const origmessage = intl.messages[messageId];
    // needed in PROD-1966
    // https://formatjs.io/docs/core-concepts/icu-syntax/#quoting--escaping
    if (!replaceVariables) {
      intl.messages[messageId] = origmessage
        .toString()
        .replaceAll('{', "'{")
        .replaceAll('}', "'}");
    }

    intl.messages[messageId] = intl.messages[messageId] + ' ';
    message = intl.formatMessage(
      {
        id: messageId,
      },
      varcontents
    );
    // restore original message
    intl.messages[messageId] = origmessage;
  } else {
    message = messageId;
  }

  // allow both undefined and string as return value
  if (!message) {
    // return undefined, if this was passed
    return message;
  } else {
    // get rid of leading and trailing spaces (s. also comment above)
    return typeof message === 'string' ? message.trim() : message;
  }
};

/** translate a single text based on a key
 *
 * @param {string} key - translation keys to be translated
 * @param {IntlShape} intl - react-intl dependency
 * @param {Record<string, any>} @optional varcontents - list of variable names with values to be formatted into the translated string
 * @return {string} translated texts
 */
export const getTranslatedText = (
  key: string,
  intl: IntlShape,
  varcontents?: Record<string, any>
): string => {
  return translateWithFallback(intl, key, varcontents);
};

/** map list of translation keys to list of translated strings
 *
 * @param {string[]} keys - array of translation keys to be translated
 * @param {IntlShape} intl - react-intl dependency
 * @param {Record<string, any>} @optional varcontents - list of variable names with values to be formatted into the translated string
 * @return {string[]} array of translated texts
 */
export const getTranslatedTexts = (
  keys: string[],
  intl: IntlShape,
  varcontents?: Record<string, any>
): string[] => {
  return keys.map((transkey: string) =>
    translateWithFallback(intl, transkey, varcontents)
  );
};

/** map list of translation keys to list of translated strings
 *
 * @param {Record<string, any>[]} objects - array of objects, containing translation keys in .value to be translated
 * @param {IntlShape} intl - react-intl dependency
 * @param {Record<string, any>} @optional varcontents - list of variable names with values to be formatted into the translated string
 * @return {Record<string, any>[]} array of translated objects (.value will be translated)
 */
export const getTranslatedObjects = (
  objects: Record<string, any>[],
  intl: IntlShape,
  varcontents?: Record<string, any>
): Record<string, any>[] => {
  return objects.map((object: Record<string, any>) => {
    return {
      ...object,
      value: translateWithFallback(intl, object.value, varcontents),
    };
  });
};

/** format a UTC timestamp as time
 *
 * @param {string | undefined} timestring - string with a UTC timestamp
 * @param {string} locale - locale as string (by intl.locale)
 * @return {string} returns a localised string time format (e.g., 14:10 for DE)
 *
 */
export const formatTime = (
  timestring: string | undefined,
  locale: string
): string => {
  return !timestring || timestring === ''
    ? ''
    : new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(Date.parse(timestring + ''));
};

/** format a UTC timestamp as date
 *
 * @param {string | undefined} datestring - string with a UTC timestamp
 * @param {string} locale - locale as string (by intl.locale)
 * @return {string} returns a localised string in format dd. month year (e.g., 17. Januar 2022)
 *
 */
export const formatDate = (
  datestring: string | undefined,
  locale: string
): string => {
  return !datestring || datestring === ''
    ? ''
    : new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      }).format(Date.parse(datestring + ''));
};

/** get all parameters from the getValuesFrom parameter in payload and removes empty var names
 *
 * @param {any} payload - the payload to be parsed for the value
 * @return {string[] | undefined} return a list of variable names
 *
 */
export const getValuesFromParam = (payload: any): string[] | undefined => {
  return payload.getValuesFrom
    ? payload.getValuesFrom.filter(
        (varname: string) => varname && varname.trim() !== ''
      )
    : undefined;
};

/** get a record of variables and their values in format { var: 'varvalue', var2: 'varvalue2' } for direct usage in translation context later
 * function also handles variables that contains arrays. The arrays are matched to _i for current loop values and _<number> for indexed access
 *
 * @param {any} payload - the payload to be parsed for the value
 * @param {MachineContext} context - the current xState machine context that is checked for variable contents
 * @return {Record<string, any> | undefined} return a record in the format values in format { var: 'varvalue', var2: 'varvalue2' }
 *
 */
export const getStringValuesParam = (
  payload: any,
  context: MachineContext
): Record<string, any> | undefined => {
  let varsForString: Record<string, any> | undefined = {};

  /** asd */
  const getValue = (value: any | undefined): string => {
    if (value === undefined || value === null) return '';
    if (value.value) return value.value;
    return value.toString();
  };

  const getIndicesValues = (
    varname: string,
    values: any[],
    varsForString: Record<string, any>
  ): Record<string, any> => {
    //
    varsForString[varname] = values.reduce((res: string, value: any) => {
      if (res !== '') {
        res = res + ', ';
      }
      res = res + getValue(value);
      return res;
    }, '');
    // 3. set VARNAME_<number>
    values.forEach((value: any, index: number) => {
      if (varsForString) varsForString[varname + '_' + index] = getValue(value);
    });

    return varsForString;
  };

  if (payload.getStringValues) {
    payload.getStringValues.forEach((varname: string | undefined) => {
      if (varsForString && varname !== undefined && varname !== '') {
        const inLoop = varname === context.userData['loopName'];
        varsForString[varname] = context.userData[varname];
        if (inLoop) {
          // loop variable
          // 1. set VARNAME_i
          varsForString[varname + '_i'] =
            context.userData[varname + '.loopValue'];

          // 1.b // check whether this is a string or an object (PROD-1244)
          if (typeof varsForString[varname + '_i'] !== 'string') {
            varsForString[varname + '_i'] = varsForString[varname + '_i'].value;
          }
          // 2. set VARNAME
          if (Array.isArray(context.userData[varname + '.arrayValues'])) {
            varsForString = getIndicesValues(
              varname,
              context.userData[varname + '.arrayValues'],
              varsForString
            );
          }
        } else {
          if (Array.isArray(context.userData[varname])) {
            // 2. set VARNAME
            varsForString = getIndicesValues(
              varname,
              context.userData[varname],
              varsForString
            );
          }
        }
      }
    });
  } else {
    varsForString = undefined;
  }

  return varsForString;
};
